import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Button,
  VStack,
  HStack,
  Spinner,
  useToast,
  Text,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

import {
  getTableList,
  getTableColumns,
  TableListItem,
  TableColumnItem,
  getDynamicForecastResult,
} from "../service/tableService";

interface AdvancedForecastViewProps {
  onClose: () => void;
}

const AdvancedForecastView: React.FC<AdvancedForecastViewProps> = ({
  onClose,
}) => {
  const [tableList, setTableList] = useState<TableListItem[]>([]);
  const [columnList, setColumnList] = useState<TableColumnItem[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState(false);

  const [tableName, setTableName] = useState("");
  const [affectedColumn, setAffectedColumn] = useState("");
  const [dateCriteria, setDateCriteria] = useState("");
  const [horizon, setHorizon] = useState("");
  const [details, setDetails] = useState("");

  const toast = useToast();
  const [forecastChartData, setForecastChartData] = useState<number[]>([]);
  const [queryChartData, setQueryChartData] = useState<any[]>([]);
  const [mergedChartData, setMergedChartData] = useState<any[]>([]);

  // Load Table List
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const result = await getTableList();
        setTableList(result);
      } catch {
        toast({
          title: "Failed to load table list",
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoadingTables(false);
      }
    };
    fetchTables();
  }, []);

  // Load Columns when table is selected
  useEffect(() => {
    if (!tableName) {
      setColumnList([]);
      return;
    }

    const fetchColumns = async () => {
      setLoadingColumns(true);
      try {
        const result = await getTableColumns(tableName);
        setColumnList(result);
      } catch {
        toast({
          title: "Failed to load column list",
          status: "error",
          isClosable: true,
        });
        setColumnList([]);
      } finally {
        setLoadingColumns(false);
      }
    };

    fetchColumns();
  }, [tableName]);

  const handleSubmit = async () => {
    if (
      !tableName ||
      !affectedColumn ||
      !dateCriteria ||
      !horizon ||
      !details
    ) {
      toast({
        title: "Please fill all fields",
        status: "warning",
        isClosable: true,
      });
      return;
    }

    try {
      const result = await getDynamicForecastResult(
        tableName,
        affectedColumn,
        horizon,
        "7",
        dateCriteria,
        details
      );

      const queryData = JSON.parse(result.queryResponseData || "[]");
      const forecastValues = result.modelResponse?.forecastedValues || [];
      setQueryChartData(queryData);
      // Step 1: map query data (historical)
      const historicalPoints = queryData.map((item: any) => ({
        label: item.Date?.split("T")[0],
        historical: item[affectedColumn] ?? null,
        forecasted: null,
      }));

      // Step 2: map forecast data
      const lastDate = new Date(historicalPoints[0]?.label || new Date());
      const forecastPoints = forecastValues.map((value: number, i: number) => {
        const date = new Date(lastDate);
        date.setDate(date.getDate() + 7 * (i + 1)); // 1 week per forecast point
        return {
          label: date.toISOString().split("T")[0],
          historical: null,
          forecasted: value,
        };
      });

      setMergedChartData([...historicalPoints.reverse(), ...forecastPoints]);
      console.log(result.message);
      toast({
        title:
          result.message == undefined ? "Forecasted success" : result.message,
        status: "success",
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to fetch forecast",
        status: "error",
        isClosable: true,
      });
      console.error(error);
    }
  };
  const handleDownloadCSV = () => {
    if (!queryChartData.length) return;

    const keys = Object.keys(queryChartData[0]);
    const csvRows = [
      keys.join(","), // header
      ...queryChartData.map((row: any) =>
        keys
          .map((k) => {
            let val = row[k];
            if (
              typeof val === "string" &&
              (val.includes(",") || val.includes('"'))
            ) {
              val = `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          })
          .join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "queryResponseData.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadJSON = () => {
    if (!queryChartData.length) return;
    const blob = new Blob([JSON.stringify(queryChartData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "queryResponseData.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box
        p={6}
        mt={10}
        borderWidth={1}
        borderRadius="lg"
        bg="white"
        shadow="sm"
      >
        <Heading size="md" mb={4}>
          📊 Advanced Forecasting
        </Heading>
        <VStack spacing={4} align="stretch">
          {/* Row 1: Table Name and Affected Column */}
          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Table Name</FormLabel>
              {loadingTables ? (
                <Spinner />
              ) : (
                <Select
                  placeholder="Select table"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                >
                  {tableList.map((table) => (
                    <option key={table.tableName} value={table.tableName}>
                      {table.tableName}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Affected Column</FormLabel>
              {loadingColumns ? (
                <Spinner />
              ) : (
                <Select
                  placeholder="Select column"
                  value={affectedColumn}
                  onChange={(e) => setAffectedColumn(e.target.value)}
                >
                  {columnList.map((col) => (
                    <option key={col.value} value={col.value}>
                      {col.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>
          </HStack>

          {/* Row 2: Date Criteria and Horizon */}
          <HStack spacing={4}>
            <FormControl>
              <FormLabel>Date Criteria</FormLabel>
              {loadingColumns ? (
                <Spinner />
              ) : (
                <Select
                  placeholder="Select date field"
                  value={dateCriteria}
                  onChange={(e) => setDateCriteria(e.target.value)}
                >
                  {columnList.map((col) => (
                    <option key={col.value} value={col.value}>
                      {col.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Horizon (in weeks)</FormLabel>
              <Select
                placeholder="Select forecast horizon"
                value={horizon}
                onChange={(e) => setHorizon(e.target.value)}
              >
                <option value="4">4 Weeks</option>
                <option value="8">8 Weeks</option>
                <option value="13">13 Weeks</option>
              </Select>
            </FormControl>
          </HStack>

          {/* Required Details - Compact */}
          <FormControl>
            <FormLabel>Mention table specific details *</FormLabel>
            <Text mt={0}>
              Enter the column value for forecasting. Eg: ItemId is Item005
            </Text>
            <Textarea
              rows={1}
              mt={3}
              resize="none"
              placeholder="Enter any specific requirements or notes..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </FormControl>

          {/* Action Buttons */}
          <HStack justify="flex-end" pt={4}>
            <Button colorScheme="blue" onClick={handleSubmit}>
              Submit
            </Button>
          </HStack>
        </VStack>
        {mergedChartData.length > 0 && (
          <Box mt={10}>
            <Heading size="sm" mb={2}>
              📊 Historical And Forecasted
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mergedChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="historical"
                  stroke="#3182CE"
                  name={`Historical (${affectedColumn})`}
                  dot={{ r: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="forecasted"
                  stroke="#2F855A"
                  name="Forecasted"
                  strokeDasharray="4 4"
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        {queryChartData.length > 0 && (
          <Box mt={4} p={4} borderWidth={1} borderRadius="md" bg="gray.50">
            <Heading size="sm" mb={2}>
              📥 Download Data
            </Heading>
            <HStack spacing={4}>
              <Button size="sm" colorScheme="green" onClick={handleDownloadCSV}>
                Download CSV
              </Button>
              <Button
                size="sm"
                colorScheme="orange"
                onClick={handleDownloadJSON}
              >
                Download JSON
              </Button>
            </HStack>
          </Box>
        )}
      </Box>
      <Button mt={5} size="sm" variant="link" colorScheme="blue" onClick={onClose}>
        ← Back to Forecast Selection
      </Button>
    </Box>
  );
};

export default AdvancedForecastView;
